import React from 'react';
import { AppIcon } from '@/components/ui/icons';
import { cn } from '@/lib/utils';
import type { NavigationContextOption } from '@/lib/navigation/navigationContexts';

interface NavigationContextMenuProps {
  contexts: NavigationContextOption[];
  activeContext: NavigationContextOption | null;
  onSelect: (context: NavigationContextOption) => void;
  onClose: () => void;
  className?: string;
}

const NavigationContextMenu: React.FC<NavigationContextMenuProps> = ({
  contexts,
  activeContext,
  onSelect,
  onClose,
  className,
}) => {
  const menuRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const handlePointerDown = (event: PointerEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) onClose();
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  return (
    <div
      ref={menuRef}
      role="menu"
      aria-label="Switch operating context"
      className={cn(
        'ax-menu-surface absolute left-2 right-2 top-full z-[120] mt-2 overflow-hidden p-1.5',
        className,
      )}
    >
      <p className="px-2.5 pb-1.5 pt-1 ax-section-label text-text-muted">
        Switch context
      </p>
      {contexts.map((context) => {
        const active = context.key === activeContext?.key;
        return (
          <button
            key={context.key}
            type="button"
            role="menuitemradio"
            aria-checked={active}
            onClick={() => {
              onSelect(context);
              onClose();
            }}
            className={cn(
              'flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left outline-none transition-colors focus-visible:ring-2 focus-visible:ring-primary-500/40',
              active ? 'bg-accent-soft text-accent-primary' : 'text-text-secondary hover:bg-surface-muted',
            )}
          >
            <span className={cn(
              'flex h-7 w-7 flex-none items-center justify-center rounded-lg',
              active ? 'bg-accent-soft text-accent-primary' : 'bg-surface-muted text-text-muted',
            )}>
              <AppIcon name={context.icon} className="h-3.5 w-3.5" />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-xs font-semibold text-current">{context.label}</span>
              <span className="block truncate text-[10px] text-text-muted">{context.description}</span>
            </span>
            {active && <AppIcon name="check" className="h-3.5 w-3.5 flex-none" />}
          </button>
        );
      })}
    </div>
  );
};

export default NavigationContextMenu;
